
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { ValidationProvider } from "vee-validate";
import { Project_Skill } from "@/generated/graphql";
import GET_PROJECT_SKILLS from "@/apollo/skills/GetProjectSkills.gql";
import GET_PROJECT_SOFT_SKILLS from "@/apollo/skills/GetProjectSoftSkills.gql";
import FormGroup from "@/components/form/FormGroupNew.vue";
import FormInputGroup from "@/components/form/FormInputGroupNew.vue";
import FormSelect from "@/components/form/FormSelectNew.vue";
import ClientGenederSelect from "@/components/autocompleteMultiselects/ClientGenederSelect.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import FormMultiselect from "@/components/form/FormMultiSelect.vue";
import FormDatepicker from "@/components/form/FormDatepicker.vue";
import FormToggle from "@/components/form/CheckboxNew.vue";
import FormCheckbox from "@/components/form/FormCheckboxNew.vue";
import UIButton from "@/components/button/ui-button.vue";
import LocationModal from "@/views/clients/LocationModal.vue";
import SkillMultiselectOption from "@/entities/SkillMultiselectOption";
import SoftSkillMultiselectOption from "@/entities/SoftSkillMultiselectOption";
import ManagersMultiselect from "@/components/autocompleteMultiselects/ManagersMultiselectNew.vue";
import CustomSkillsMultiselect from "@/components/autocompleteMultiselects/customOptions/CustomSkillsMultiselectNew.vue";
import CustomSoftSkillsMultiselect from "@/components/autocompleteMultiselects/customOptions/CustomSoftSkillsMultiselectNew.vue";
import StandardizedPhoneInput from "@/components/form/StandardizedPhoneInput.vue";

import _ from "lodash";

@Component({
  components: {
    ValidationProvider,
    ManagersMultiselect,
    FormGroup,
    FormInputGroup,
    LocationModal,
    FormSelect,
    ClientGenederSelect,
    FormTextarea,
    FormMultiselect,
    FormDatepicker,
    FormToggle,
    FormCheckbox,
    UIButton,
    CustomSkillsMultiselect,
    CustomSoftSkillsMultiselect,
    StandardizedPhoneInput,
  },
  apollo: {
    skillsIds: {
      query: GET_PROJECT_SKILLS,
      variables() {
        return {
          projectId: this.formData.id,
        };
      },
      update(data): void {
        if (data.project_skill.length > 0) {
          this.hasSkill = false;
        }
        const skillsIds = data.project_skill.map(
          ({ skill_id }: Project_Skill) => skill_id
        );
        this.projectSkillsIds = skillsIds;

        this.editedProjectSkillsIds = skillsIds;
      },
      skip(): boolean {
        return !Boolean(this.formData?.id);
      },
    },
    softSkillsIds: {
      query: GET_PROJECT_SOFT_SKILLS,
      variables() {
        return {
          projectId: this.formData.id,
        };
      },
      update(data): void {
        const softSkillsIds = data.project_soft_skill.map(
          ({ soft_skill_id }: any) => soft_skill_id
        );
        this.projectSoftSkillsIds = softSkillsIds;
        this.editedProjectSoftSkillsIds = softSkillsIds;
      },
      skip(): boolean {
        return !Boolean(this.formData?.id);
      },
    },
  },
})
export default class CreateClientTab extends Vue {
  @Prop({ type: Object, required: true }) readonly formData!: Record<
    string,
    any
  >;
  @Prop({ type: Number, required: false }) readonly initialStep!: number;
  @Prop({ type: Boolean, default: "null" }) readonly isDrafted!: boolean;
  @Prop({ type: Object }) readonly invalid: any;

  localFormData: Record<string, any> = {};
  projectTagsIds: number[] = [];
  tagsError: string = "Tags are required";
  newSkillsIds: number[] = [];
  projectSkillsIds: number[] = [];
  hasSkill: boolean = true;
  editedProjectSkillsIds: number[] = [];
  newSkillsNames: string[] = [];
  projectSoftSkillsIds: number[] = [];
  editedProjectSoftSkillsIds: number[] = [];
  newSoftSkillsNames: string[] = [];
  newSoftSkillsIds: number[] = [];

  @Watch("formData", { immediate: true, deep: true })
  onFormDataChange(newVal: Record<string, any>) {
    this.localFormData = _.cloneDeep(newVal);

    // Initialize firstname and lastname from client_name if they don't exist
    if (newVal.client_name && !newVal.firstname && !newVal.lastname) {
      const nameParts = newVal.client_name.split(" ");
      this.localFormData.firstname = nameParts[0] || "";
      this.localFormData.lastname = nameParts.slice(1).join(" ") || "";
    }
  }

  created() {
    this.localFormData = _.cloneDeep(this.formData);

    // Initialize firstname and lastname from client_name if they don't exist
    if (
      this.formData.client_name &&
      !this.formData.firstname &&
      !this.formData.lastname
    ) {
      const nameParts = this.formData.client_name.split(" ");
      this.localFormData.firstname = nameParts[0] || "";
      this.localFormData.lastname = nameParts.slice(1).join(" ") || "";
    }
  }
  updateDate(val: any) {
    // Convert to Date object
    const date = new Date(val);

    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());

    const formattedDate = date.toISOString().split("T")[0];

    this.localFormData["dob"] = formattedDate;
  }

  updateField(field: string, value: any) {
    this.localFormData[field] = value;

    // Combine firstname and lastname into client_name
    if (field === "firstname" || field === "lastname") {
      const firstname = this.localFormData.firstname || "";
      const lastname = this.localFormData.lastname || "";
      this.localFormData.client_name = `${firstname} ${lastname}`.trim();
    }

    this.$emit("update:formData", this.localFormData);
  }

  handleManagerInput(managersIds: string[]): void {
    this.localFormData["manager_id"] = managersIds;
    this.$emit("update:formData", this.localFormData);
  }
  updateSkills(
    skillsIds: number[],
    newSkillsNames: string[],
    allChosenSkills: SkillMultiselectOption[]
  ): void {
    this.editedProjectSkillsIds = skillsIds;
    this.newSkillsNames = newSkillsNames;
    this.newSkillsIds = skillsIds;
    this.localFormData["new_skills"] = newSkillsNames;
    this.localFormData["projectSkillsIds"] = this.projectSkillsIds;
    this.localFormData["skillsIds"] = skillsIds;
    this.localFormData["personalSkills"] = allChosenSkills?.filter((skill) =>
      Boolean(skill.user_id)
    );
    this.hasSkill = !(
      skillsIds?.length > 0 ||
      newSkillsNames?.length > 0 ||
      allChosenSkills?.length > 0
    );

    this.$emit("update:formData", this.localFormData);
  }

  updateSoftSkills(
    softSkillsIds: number[],
    newSoftSkillsNames: string[],
    allChosenSoftSkills: SoftSkillMultiselectOption[]
  ): void {
    this.editedProjectSoftSkillsIds = softSkillsIds;
    this.newSoftSkillsNames = newSoftSkillsNames;
    this.newSoftSkillsIds = softSkillsIds;
    this.localFormData["new_soft_skills"] = newSoftSkillsNames;
    this.localFormData["projectSoftSkillsIds"] = this.projectSoftSkillsIds;
    this.localFormData["softSkillsIds"] = softSkillsIds;
    this.localFormData["personalSoftSkills"] = allChosenSoftSkills?.filter((softSkill) =>
      Boolean(softSkill.user_id)
    );

    this.$emit("update:formData", this.localFormData);
  }

  saveDraft() {
    const cleanedData = _.omit(this.localFormData, ["__typename"]);
    this.$emit("save-draft", cleanedData);
  }

  async submit() {
    try {
      const cleanedData = _.omit(this.localFormData, ["__typename"]);
      await this.$emit("submit", cleanedData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  }

  handlePhoneValidation(validation: {
    isValid: boolean;
    message?: string;
    value: string;
  }) {
    // Additional phone validation handling if needed
    // The StandardizedPhoneInput component already handles validation
    // This method can be used for custom validation logic or side effects
    if (!validation.isValid && validation.message) {
      console.debug("Phone validation error:", validation.message);
    }
  }
}
